import { HOLDING_SLA_MS } from "./escalation";
import { isWindowGated, type Channel } from "./types";
import { STANDARD_WINDOW_MS, windowState, type WindowState } from "./window";

/**
 * T2, Agent Brains section 10.1: the situation object. "There is no second
 * version of reality to sync", so this is a PURE READ MODEL over the event
 * log. Nothing stores it, so nothing can edit it freehand: it is derived on
 * every read from the events and an injected clock.
 *
 * Pure means: no IO, no clock access, no randomness, and the input is never
 * mutated. The reducer sorts by (occurredAt, recordedAt, id) first, so any
 * shuffle of the same events gives an equal situation, and a replay of the log
 * reconstructs the same one (CLAUDE.md section 5).
 *
 * It is informational. Nothing reads it inside a send gate: the window state
 * here is a courtesy copy, and the send gate keeps reading
 * `threads.last_customer_message_at` (Inbox Composer section 6, T3).
 *
 * Payloads carry ids, codes, counts and ISO times only (section 10), so this
 * file never touches customer text.
 */

export type Stage =
  | "new"
  | "engaged"
  | "consult_booked"
  | "consult_held"
  | "deposit"
  | "won"
  | "lost"
  | "ghosted"
  | "reactivation";

const STAGES: readonly Stage[] = [
  "new",
  "engaged",
  "consult_booked",
  "consult_held",
  "deposit",
  "won",
  "lost",
  "ghosted",
  "reactivation",
];

export interface LedgerEvent {
  readonly id: string;
  readonly type: string;
  readonly occurredAt: Date;
  readonly recordedAt: Date;
  readonly agentId: string;
  readonly agentVersion: string;
  readonly contactId: string | null;
  readonly threadId: string | null;
  readonly payload: unknown;
}

export interface SituationPromise {
  readonly promiseId: string;
  readonly kind: "check_and_reply" | "consult_scheduled";
  readonly madeBy: string;
  readonly madeAt: string;
  readonly byWhen: string | null;
  readonly status: "pending_send" | "open" | "overdue";
  readonly sourceEventId: string;
}

export type ActionOwner =
  | { readonly type: "agent"; readonly agentId: string }
  | { readonly type: "human"; readonly role: "owner" };

export interface PendingAction {
  readonly kind: "approve_draft" | "accept_handoff" | "keep_promise" | "answer_escalation";
  readonly owner: ActionOwner;
  readonly ref: string;
  readonly since: string;
  readonly dueAt: string | null;
}

export interface OpenEscalation {
  readonly draftId: string;
  readonly since: string;
  readonly holdingSent: boolean;
  readonly amber: boolean;
}

export interface Situation {
  readonly schemaVersion: 1;
  readonly clientId: string;
  readonly threadId: string;
  readonly contactId: string;
  readonly asOf: { readonly eventId: string | null; readonly occurredAt: string | null; readonly eventCount: number };
  readonly stage: {
    readonly value: Stage;
    readonly since: string | null;
    readonly sourceEventId: string | null;
    readonly explicit: boolean;
  };
  readonly window: {
    readonly state: WindowState | "no_inbound" | "not_applicable";
    readonly lastCustomerMessageAt: string | null;
    readonly closesAt: string | null;
  };
  readonly promises: readonly SituationPromise[];
  readonly pendingActions: readonly PendingAction[];
  readonly lastAgent: {
    readonly agentId: string;
    readonly agentVersion: string;
    readonly eventType: string;
    readonly at: string;
  } | null;
  readonly openEscalations: readonly OpenEscalation[];
  readonly frozen: boolean;
  readonly unhandledKinds: number;
}

export interface DeriveSituationInput {
  readonly clientId: string;
  readonly threadId: string;
  readonly contactId: string;
  readonly channel: Channel;
  readonly events: readonly LedgerEvent[];
  readonly now: Date;
  /**
   * The agent ids the roster owns, for the `lastAgent` rule. An INPUT, not an
   * import: the roster is T3's file and this reducer must not depend on it.
   * System actors (ingest, worker, owner, gateway) are simply not in the set.
   */
  readonly ownedAgentIds: ReadonlySet<string>;
}

/**
 * The kinds that close an open draft escalation, in the order the design
 * lists them. Fixed from code: the owner's decision (`draft.approved`,
 * `draft.rejected`), the run finishing (`reply.finalized`), the send leaving
 * the building (`draft.transmitted`), and the draft being superseded, which
 * the ledger records as a `send.withheld` restraint row with gate
 * `owner_answered` (ingest.ts supersedePendingDrafts). Any other
 * `send.withheld` (a refused transmit) does NOT close it: the owner's
 * approval already did, and a refusal says nothing was answered.
 */
export const ESCALATION_CLOSE_KINDS = [
  "draft.approved",
  "draft.rejected",
  "reply.finalized",
  "draft.transmitted",
  "send.withheld",
] as const;

/** The gate a `send.withheld` row carries when the owner answered in the inbox first. */
const SUPERSEDED_GATE = "owner_answered";

/** Every kind a rule below reads. A kind outside this set is counted, not ignored. */
const HANDLED_KINDS: ReadonlySet<string> = new Set([
  "message.received",
  "lead.created",
  "stage.changed",
  "reply.finalized",
  "draft.created",
  "draft.blocked",
  "draft.approved",
  "draft.rejected",
  "draft.transmitted",
  "send.withheld",
  "message.echoed",
  "escalation.holding_sent",
  "alert.escalation_amber",
  "reply.frozen",
  "thread.unfrozen",
  "appointment.booked",
  "appointment.attended",
  "appointment.no_show",
  "deposit.paid",
  "outcome.recorded",
  "outcome.auto_ghost",
  "outcome.reversed",
  "outcome.ghost_reversed",
  "promise.made",
  "promise.closed",
  "handoff.offered",
  "handoff.accepted",
  "handoff.declined",
]);

/** Implicit stage by milestone rank. A later, weaker milestone never pulls the stage back. */
const RANK: Readonly<Record<Exclude<Stage, "reactivation">, number>> = {
  new: 0,
  engaged: 1,
  consult_booked: 2,
  consult_held: 3,
  deposit: 4,
  won: 5,
  lost: 5,
  ghosted: 5,
};

const OUTCOME_STAGE: Readonly<Record<string, Stage>> = {
  deposit: "deposit",
  sold: "won",
  lost: "lost",
  ghosted: "ghosted",
};

function obj(p: unknown): Readonly<Record<string, unknown>> {
  return typeof p === "object" && p !== null && !Array.isArray(p) ? (p as Record<string, unknown>) : {};
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim().length > 0 ? v : null;
}

function isoOrNull(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : new Date(t).toISOString();
}

function compareEvents(a: LedgerEvent, b: LedgerEvent): number {
  const o = a.occurredAt.getTime() - b.occurredAt.getTime();
  if (o !== 0) return o;
  const r = a.recordedAt.getTime() - b.recordedAt.getTime();
  if (r !== 0) return r;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

interface PromiseState {
  readonly promiseId: string;
  readonly kind: "check_and_reply" | "consult_scheduled";
  readonly madeBy: string;
  readonly madeAt: Date;
  readonly draftId: string | null;
  readonly slaMs: number | null;
  readonly dueAt: string | null;
  readonly sourceEventId: string;
  openedAt: Date | null;
  /**
   * TA-1361: the first shop outbound after the promise opened has been seen,
   * its own line going out (the transmit, or the owner's hand-send in draft
   * mode). An owner-typed outbound after it answers; that one does not.
   */
  ownLineSeen: boolean;
  closed: boolean;
}

interface DraftState {
  readonly since: Date;
  readonly needsOwnerKnowledge: boolean;
  readonly blocked: boolean;
}

interface HandoffState {
  readonly toAgent: string;
  readonly since: Date;
  readonly dueAckAt: string | null;
}

export function deriveSituation(input: DeriveSituationInput): Situation {
  const relevant = input.events
    .filter((e) => (e.threadId === null || e.threadId === input.threadId) && (e.contactId === null || e.contactId === input.contactId))
    .slice()
    .sort(compareEvents);

  let unhandled = 0;

  // First pass: outcome reversals, so an outcome a later event withdrew never counts.
  const reversedOutcomes = new Set<string>();
  for (const e of relevant) {
    if (e.type === "outcome.reversed" || e.type === "outcome.ghost_reversed") {
      const id = str(obj(e.payload).outcomeId);
      if (id !== null) reversedOutcomes.add(id);
    }
  }

  // TA-1361: the Meta ids the agent's own transmits carry (every bubble of a
  // paced one), read first because Meta stamps an echo at the send, before the
  // transmit settles. An echo under one of these ids is the agent's own send
  // coming back, which its `draft.transmitted` already speaks for: it is never
  // read as the owner typing in the inbox.
  const transmittedMessageIds = new Set<string>();
  for (const e of relevant) {
    if (e.type !== "draft.transmitted") continue;
    const p = obj(e.payload);
    const one = str(p.platformMessageId);
    if (one !== null) transmittedMessageIds.add(one);
    if (Array.isArray(p.platformMessageIds)) {
      for (const id of p.platformMessageIds) {
        const s = str(id);
        if (s !== null) transmittedMessageIds.add(s);
      }
    }
  }

  // Held in one object so the closure's writes are visible to the reads below.
  const st: {
    explicit: { value: Stage; at: Date; id: string } | null;
    implicit: { value: Stage; rank: number; at: Date; id: string } | null;
  } = { explicit: null, implicit: null };
  const milestone = (value: Exclude<Stage, "reactivation">, e: LedgerEvent): void => {
    const rank = RANK[value];
    if (st.implicit === null || rank >= st.implicit.rank) {
      st.implicit = { value, rank, at: e.occurredAt, id: e.id };
    }
  };

  let lastInbound: Date | null = null;
  let frozen = false;
  let lastAgent: Situation["lastAgent"] = null;

  const promises = new Map<string, PromiseState>();
  const closedPromiseIds = new Set<string>();
  const drafts = new Map<string, DraftState>();
  const closedDrafts = new Set<string>();
  // Drafts that ended without a send: a promise waiting on one can never open.
  const unsentDrafts = new Set<string>();
  const holdingSent = new Set<string>();
  const amber = new Set<string>();
  const offered = new Map<string, HandoffState>();
  const resolvedHandoffs = new Set<string>();
  // TA-1361: owner-typed outbounds already read, by Meta id. recordEcho writes
  // a `message.echoed` and a `send.withheld` per superseded draft for ONE
  // message, all under its id, so the id is read once.
  const ownerOutbounds = new Set<string>();

  /**
   * TA-1361 (section 10.1, the owed read's rule in packages/db/src/owedAnswer.ts
   * and core textEcho.ts, row 157): an owner-typed outbound on the thread. The
   * payloads carry no text and no draft id, so the echo of the promise's own
   * released line cannot be told from a hand-typed answer by what it says. The
   * owed read's own first-message rule decides instead: the first shop outbound
   * after the release is the released line going out (the hand-send in draft
   * mode), the promise being made, not kept. Any owner-typed outbound after it
   * answers and closes the promise. A promise still pending_send, or a consult
   * promise, is untouched, as with a later transmit.
   */
  const ownerOutbound = (e: LedgerEvent, platformMessageId: string): void => {
    if (transmittedMessageIds.has(platformMessageId) || ownerOutbounds.has(platformMessageId)) return;
    ownerOutbounds.add(platformMessageId);
    for (const pr of promises.values()) {
      if (pr.closed || pr.kind !== "check_and_reply" || pr.openedAt === null) continue;
      if (e.occurredAt.getTime() <= pr.openedAt.getTime()) continue;
      if (pr.ownLineSeen) pr.closed = true;
      else pr.ownLineSeen = true;
    }
  };

  for (const e of relevant) {
    const p = obj(e.payload);
    if (input.ownedAgentIds.has(e.agentId)) {
      lastAgent = { agentId: e.agentId, agentVersion: e.agentVersion, eventType: e.type, at: e.occurredAt.toISOString() };
    }
    if (!HANDLED_KINDS.has(e.type)) {
      unhandled += 1;
      continue;
    }

    switch (e.type) {
      case "message.received":
        lastInbound = e.occurredAt;
        milestone("new", e);
        break;
      case "lead.created":
        milestone("new", e);
        break;
      case "stage.changed": {
        const to = str(p.to);
        if (to !== null && (STAGES as readonly string[]).includes(to)) {
          st.explicit = { value: to as Stage, at: e.occurredAt, id: e.id };
        } else {
          unhandled += 1;
        }
        break;
      }
      case "appointment.booked":
        milestone("consult_booked", e);
        break;
      case "appointment.attended":
        milestone("consult_held", e);
        break;
      case "appointment.no_show":
        break;
      case "deposit.paid":
        milestone("deposit", e);
        break;
      case "outcome.recorded":
      case "outcome.auto_ghost": {
        const id = str(p.outcomeId);
        const stage = OUTCOME_STAGE[str(p.label) ?? ""];
        if (stage !== undefined && !(id !== null && reversedOutcomes.has(id))) {
          milestone(stage as Exclude<Stage, "reactivation">, e);
        }
        break;
      }
      case "outcome.reversed":
      case "outcome.ghost_reversed":
        break;
      case "reply.finalized": {
        const draftId = str(p.draftId);
        if (p.status === "sent") {
          milestone("engaged", e);
          if (draftId !== null) {
            for (const pr of promises.values()) {
              if (pr.draftId === draftId) {
                if (pr.openedAt === null) pr.openedAt = e.occurredAt;
              } else if (
                !pr.closed &&
                pr.kind === "check_and_reply" &&
                pr.openedAt !== null &&
                e.occurredAt.getTime() > pr.openedAt.getTime()
              ) {
                // TA-1361: the owner released another draft after this promise
                // opened, which in draft mode is the shop's next word (the
                // owed read's newest released draft replaces this one the same
                // way). Closed as a later transmit closes it.
                pr.closed = true;
              }
            }
          }
        }
        if (draftId !== null) closedDrafts.add(draftId);
        break;
      }
      case "draft.transmitted": {
        const draftId = str(p.draftId);
        if (draftId === null) break;
        closedDrafts.add(draftId);
        for (const pr of promises.values()) {
          if (pr.closed) continue;
          if (pr.draftId === draftId) {
            if (pr.openedAt === null) pr.openedAt = e.occurredAt;
            // TA-1361: its own line went out, so the next owner-typed outbound answers.
            pr.ownLineSeen = true;
          } else if (pr.kind === "check_and_reply" && pr.openedAt !== null && e.occurredAt.getTime() > pr.openedAt.getTime()) {
            // A later shop outbound, after this promise was opened by its own
            // send, answered the question. A promise still pending_send is not
            // closed by an unrelated draft going out. A
            // consult promise is kept by the consult, not by a message, so it
            // is not closed this way.
            pr.closed = true;
          }
        }
        break;
      }
      case "draft.approved":
      case "draft.rejected": {
        const draftId = str(p.draftId);
        if (draftId !== null) closedDrafts.add(draftId);
        if (draftId !== null && e.type === "draft.rejected") unsentDrafts.add(draftId);
        break;
      }
      case "send.withheld": {
        const draftId = str(p.draftId);
        if (draftId !== null && p.gate === SUPERSEDED_GATE) {
          closedDrafts.add(draftId);
          unsentDrafts.add(draftId);
        }
        // TA-1361: a supersession is the owner typing in the inbox, the same
        // message as its echo (one Meta id), so it is read through the same rule.
        const mid = str(p.platformMessageId);
        if (p.gate === SUPERSEDED_GATE && mid !== null) ownerOutbound(e, mid);
        break;
      }
      case "message.echoed": {
        // TA-292: a shop outbound Meta echoed back, the owner's or (TA-427)
        // the agent's own; `ownerOutbound` sets the agent's own aside.
        const mid = str(p.platformMessageId);
        if (mid === null) {
          // Cannot be told from the agent's own send or deduplicated without its id: loud, not guessed.
          unhandled += 1;
          break;
        }
        ownerOutbound(e, mid);
        break;
      }
      case "draft.created":
      case "draft.blocked": {
        const draftId = str(p.draftId);
        if (draftId === null) {
          // Cannot be tracked or closed without its id: loud, not guessed.
          unhandled += 1;
          break;
        }
        if (!drafts.has(draftId)) {
          drafts.set(draftId, {
            since: e.occurredAt,
            needsOwnerKnowledge: p.needsOwnerKnowledge === true,
            blocked: e.type === "draft.blocked",
          });
        }
        break;
      }
      case "escalation.holding_sent": {
        const draftId = str(p.draftId);
        if (draftId !== null) holdingSent.add(draftId);
        break;
      }
      case "alert.escalation_amber": {
        const draftId = str(p.draftId);
        if (draftId !== null) amber.add(draftId);
        break;
      }
      case "reply.frozen":
        frozen = true;
        break;
      case "thread.unfrozen":
        frozen = false;
        break;
      case "promise.made": {
        const promiseId = str(p.promiseId);
        const kind = p.kind === "check_and_reply" || p.kind === "consult_scheduled" ? p.kind : null;
        if (promiseId === null || kind === null) {
          unhandled += 1;
          break;
        }
        if (promises.has(promiseId)) break;
        const draftId = str(p.draftId);
        promises.set(promiseId, {
          promiseId,
          kind,
          madeBy: e.agentId,
          madeAt: e.occurredAt,
          draftId,
          slaMs: typeof p.slaMs === "number" && Number.isFinite(p.slaMs) ? p.slaMs : null,
          dueAt: isoOrNull(p.dueAt),
          sourceEventId: e.id,
          // No draft to wait on: the promise is live from the moment it was made.
          openedAt: draftId === null ? e.occurredAt : null,
          ownLineSeen: false,
          closed: false,
        });
        break;
      }
      case "promise.closed": {
        const promiseId = str(p.promiseId);
        if (promiseId !== null) closedPromiseIds.add(promiseId);
        break;
      }
      case "handoff.offered": {
        const handoffId = str(p.handoffId);
        const toAgent = str(p.toAgent);
        if (handoffId === null || toAgent === null) {
          unhandled += 1;
          break;
        }
        if (!offered.has(handoffId)) {
          offered.set(handoffId, { toAgent, since: e.occurredAt, dueAckAt: isoOrNull(p.dueAckAt) });
        }
        break;
      }
      case "handoff.accepted":
      case "handoff.declined": {
        const handoffId = str(p.handoffId);
        if (handoffId !== null) resolvedHandoffs.add(handoffId);
        break;
      }
    }
  }

  // Stage.
  const stage: Situation["stage"] =
    st.explicit !== null
      ? { value: st.explicit.value, since: st.explicit.at.toISOString(), sourceEventId: st.explicit.id, explicit: true }
      : st.implicit !== null
        ? { value: st.implicit.value, since: st.implicit.at.toISOString(), sourceEventId: st.implicit.id, explicit: false }
        : { value: "new", since: null, sourceEventId: null, explicit: false };

  // Window: informational only. Off the gated channels it is not applicable.
  const lastCustomerMessageAt = lastInbound === null ? null : (lastInbound as Date).toISOString();
  let window: Situation["window"];
  if (!isWindowGated(input.channel)) {
    window = { state: "not_applicable", lastCustomerMessageAt, closesAt: null };
  } else if (lastInbound === null) {
    window = { state: "no_inbound", lastCustomerMessageAt: null, closesAt: null };
  } else {
    const last = lastInbound as Date;
    window = {
      state: windowState({ lastCustomerMessageAt: last, now: input.now }),
      lastCustomerMessageAt,
      closesAt: new Date(last.getTime() + STANDARD_WINDOW_MS).toISOString(),
    };
  }

  // Promises.
  const nowMs = input.now.getTime();
  const livePromises: SituationPromise[] = [];
  for (const pr of promises.values()) {
    if (pr.closed || closedPromiseIds.has(pr.promiseId)) continue;
    // Its draft was rejected, superseded or blocked and never went out (nothing writes promise.closed yet).
    if (pr.openedAt === null && pr.draftId !== null && (unsentDrafts.has(pr.draftId) || drafts.get(pr.draftId)?.blocked === true)) continue;
    let byWhen: string | null = null;
    if (pr.openedAt !== null) {
      byWhen = pr.dueAt ?? (pr.slaMs !== null ? new Date(pr.openedAt.getTime() + pr.slaMs).toISOString() : null);
    }
    const status: SituationPromise["status"] =
      pr.openedAt === null ? "pending_send" : byWhen !== null && nowMs > Date.parse(byWhen) ? "overdue" : "open";
    livePromises.push({
      promiseId: pr.promiseId,
      kind: pr.kind,
      madeBy: pr.madeBy,
      madeAt: pr.madeAt.toISOString(),
      byWhen,
      status,
      sourceEventId: pr.sourceEventId,
    });
  }
  livePromises.sort((a, b) => (a.madeAt < b.madeAt ? -1 : a.madeAt > b.madeAt ? 1 : a.promiseId < b.promiseId ? -1 : a.promiseId > b.promiseId ? 1 : 0));

  // Escalations and approvals.
  const openEscalations: OpenEscalation[] = [];
  const pending: PendingAction[] = [];
  for (const [draftId, d] of drafts) {
    if (closedDrafts.has(draftId)) continue;
    const since = d.since.toISOString();
    if (!d.blocked) {
      pending.push({ kind: "approve_draft", owner: { type: "human", role: "owner" }, ref: draftId, since, dueAt: null });
    }
    if (d.needsOwnerKnowledge) {
      openEscalations.push({ draftId, since, holdingSent: holdingSent.has(draftId), amber: amber.has(draftId) });
      pending.push({
        kind: "answer_escalation",
        owner: { type: "human", role: "owner" },
        ref: draftId,
        since,
        dueAt: new Date(d.since.getTime() + HOLDING_SLA_MS).toISOString(),
      });
    }
  }
  openEscalations.sort((a, b) => (a.since < b.since ? -1 : a.since > b.since ? 1 : a.draftId < b.draftId ? -1 : a.draftId > b.draftId ? 1 : 0));

  for (const pr of livePromises) {
    pending.push({ kind: "keep_promise", owner: { type: "agent", agentId: pr.madeBy }, ref: pr.promiseId, since: pr.madeAt, dueAt: pr.byWhen });
  }
  for (const [handoffId, h] of offered) {
    if (resolvedHandoffs.has(handoffId)) continue;
    pending.push({
      kind: "accept_handoff",
      owner: { type: "agent", agentId: h.toAgent },
      ref: handoffId,
      since: h.since.toISOString(),
      dueAt: h.dueAckAt,
    });
  }
  pending.sort((a, b) => {
    if (a.since !== b.since) return a.since < b.since ? -1 : 1;
    if (a.kind !== b.kind) return a.kind < b.kind ? -1 : 1;
    return a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0;
  });

  const last = relevant[relevant.length - 1];
  return {
    schemaVersion: 1,
    clientId: input.clientId,
    threadId: input.threadId,
    contactId: input.contactId,
    asOf: {
      eventId: last?.id ?? null,
      occurredAt: last ? last.occurredAt.toISOString() : null,
      eventCount: relevant.length,
    },
    stage,
    window,
    promises: livePromises,
    pendingActions: pending,
    lastAgent,
    openEscalations,
    frozen,
    unhandledKinds: unhandled,
  };
}
